import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PanResponder, Platform, ScrollView, StyleSheet, Text, TouchableWithoutFeedback, View } from 'react-native';
import { useTheme } from '@rneui/themed';
import { setHours, setMinutes, isWithinInterval, isSameDay } from 'date-fns';
import PropTypes from 'prop-types';
import Event from './Event';
import * as Utils from './utils';

const HourGuideColumn = React.memo(
    props => {
        const { theme } = useTheme();
        const cellHeight = props?.cellHeight;
        const hour = props?.hour;

        return (
            <View style={{ height: cellHeight, ...theme?.appComponents?.umbraco?.EventCalendar?.Body?.hourGuideColumn }}>
                <Text style={theme?.appComponents?.umbraco?.EventCalendar?.Body?.hourGuideColumnText}>{Utils.formatHour(hour)}</Text>
            </View>
        );
    },
    () => {
        return true;
    }
);

const HourCell = props => {
    const { theme } = useTheme();
    const cellHeight = props?.cellHeight;
    const onPress = props?.onPress;
    const date = props?.date;
    const hour = props?.hour;

    return (
        <TouchableWithoutFeedback onPress={() => onPress && onPress(setMinutes(setHours(new Date(date), hour), 0))}>
            <View
                style={{
                    ...theme?.appComponents?.umbraco?.EventCalendar?.CommonStyles.dateCell,
                    height: cellHeight,
                    backgroundColor: Utils.isToday(date)
                        ? theme?.appComponents?.umbraco?.EventCalendar?.CommonStyles?.isToday
                        : theme?.appComponents?.umbraco?.EventCalendar?.CommonStyles.dateCell.backgroundColor
                }}
            />
        </TouchableWithoutFeedback>
    );
};

const Body = props => {
    const { theme } = useTheme();
    const containerHeight = props?.containerHeight;
    const cellHeight = props?.cellHeight;
    const dateRange = props?.dateRange;
    const style = props?.style || {};
    const onCellPress = props?.onCellPress;
    const events = props?.events;
    const onEventPress = props?.onEventPress;
    const eventCellStyle = props?.eventCellStyle;
    const showTime = props?.showTime ?? true;
    const scrollOffsetMinutes = props?.scrollOffsetMinutes;
    const onSwipeHorizontal = props?.onSwipeHorizontal;
    const startHour = props?.startHour;
    const endHour = props?.endHour;
    const scrollViewRef = useRef(null);
    const [now, setNow] = useState(new Date());
    const [panHandled, setPanHandled] = useState(false);

    useEffect(() => {
        if (scrollViewRef?.current && scrollOffsetMinutes) {
            setTimeout(
                () => {
                    scrollViewRef?.current.scrollTo({
                        y: (cellHeight * scrollOffsetMinutes) / 60,
                        animated: false
                    });
                },
                Platform.OS === 'web' ? 0 : 10
            );
        }
    }, [cellHeight, scrollOffsetMinutes]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            return setNow(new Date());
        }, 2 * 60 * 1000);

        return () => clearInterval(intervalId);
    }, []);

    const hours = useMemo(() => Utils.getHours(startHour, endHour), [endHour, startHour]);

    const panResponder = useMemo(() => {
        return PanResponder.create({
            onMoveShouldSetPanResponder: (evt, state) => {
                const dx = state.dx;
                const dy = state.dy;

                return dx > 2 || dx < -2 || dy > 2 || dy < -2;
            },
            onPanResponderMove: function (evt, state) {
                const dy = state.dy;
                const dx = state.dx;

                if (dy < -1 * Utils.SWIPE_THRESHOLD || Utils.SWIPE_THRESHOLD < dy || panHandled) {
                    return;
                }
                if (dx < -1 * Utils.SWIPE_THRESHOLD) {
                    if (onSwipeHorizontal) {
                        onSwipeHorizontal('LEFT');
                    }

                    setPanHandled(true);
                    return;
                }
                if (dx > Utils.SWIPE_THRESHOLD) {
                    if (onSwipeHorizontal) {
                        onSwipeHorizontal('RIGHT');
                    }

                    setPanHandled(true);
                    return;
                }
            },
            onPanResponderEnd: () => {
                setPanHandled(false);
            }
        });
    }, [panHandled, onSwipeHorizontal]);

    const panHandlers = Platform.OS === 'web' ? panResponder.panHandlers : undefined;

    const handleCellPress = useCallback(
        date => {
            onCellPress && onCellPress(date.toDate());
        },
        [onCellPress]
    );

    return (
        <ScrollView
            ref={scrollViewRef}
            scrollEventThrottle={32}
            showsVerticalScrollIndicator={false}
            style={[{ height: containerHeight - cellHeight * 3 }, style]}
            {...panHandlers}
        >
            <View style={{ flexDirection: 'row', flex: 1 }}>
                <View style={{ borderColor: '#eee', borderLeftWidth: StyleSheet.hairlineWidth, width: 80 }}>
                    {hours.map(hour => {
                        return <HourGuideColumn key={hour} cellHeight={cellHeight} hour={hour} />;
                    })}
                </View>
                {dateRange.map((date, index) => {
                    return (
                        <View key={index} style={{ flex: 1 }}>
                            {hours.map(hour => {
                                return <HourCell key={hour} cellHeight={cellHeight} date={date} hour={hour} onPress={() => handleCellPress(date)} />;
                            })}
                            {events
                                .filter(event => {
                                    return isSameDay(date, event.start) || isSameDay(date, event.end) || isWithinInterval(date, { start: event.start, end: event.end });
                                })
                                .map(event => {
                                    return (
                                        <Event
                                            key={event.id}
                                            event={event}
                                            eventCellStyle={eventCellStyle || { backgroundColor: event.backgroundColor }}
                                            eventCount={Utils.getCountOfEventsAtEvent(event, events)}
                                            eventOrder={Utils.getOrderOfEvent(event, events)}
                                            onEventPress={onEventPress}
                                            showTime={showTime}
                                        />
                                    );
                                })}
                            {Utils.isToday(date) && (
                                <View style={[theme?.appComponents?.umbraco?.EventCalendar?.Body?.nowIndicator, { top: `${Utils.getRelativeTopInDay(now)}%` }]} />
                            )}
                        </View>
                    );
                })}
            </View>
        </ScrollView>
    );
};

Body.propTypes = {
    containerHeight: PropTypes.number,
    cellHeight: PropTypes.number,
    dateRange: PropTypes.arrayOf(Date),
    style: PropTypes.object,
    onCellPress: PropTypes.func,
    events: PropTypes.arrayOf(Object),
    onEventPress: PropTypes.func,
    eventCellStyle: PropTypes.object,
    showTime: PropTypes.bool,
    scrollOffsetMinutes: PropTypes.number,
    onSwipeHorizontal: PropTypes.func
};

export default React.memo(Body);
